import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import { ResourceLoading } from "../../../../components/utils/ResourceLoading";
import { ResourceError } from "../../../../components/utils/ResourceError";
import { ResourceEmpty } from "../../../../components/utils/ResourceEmpty";
import DashboardHeader from "../../../../components/dashboard/dashboardHeader";
import { mapErrors } from "../../../../components/Utils";
import { fetchUser } from "../../../../api/users";
import { UserInfo } from "../../../../components/dashboard/company/users/user-info";
import { UserEmployment } from "../../../../components/dashboard/company/users/user-employment";
import { UserWorkSchedule } from "../../../../components/dashboard/company/users/user-work-schedule";
import { UserRoles } from "../../../../components/dashboard/company/users/user-roles";
import { UserPasswordAccess } from "../../../../components/dashboard/company/users/user-password-access";
import JournalSection from "../../../../components/dashboard/journaling/JournalSection";
import TextDocumentsSection from "../../../../components/dashboard/text-documents/TextDocumentsSection";
import { PMNotice } from "../../../../components/utils/notifications";

export const User = (props) => {
  const { t } = useTranslation();
  const { id } = useParams();
  const dispatch = useDispatch();

  const [itemState, setItemState] = useState({
    isLoading: true,
    error: false,
    errors: [],
    user: {},
  });

  // Deconstruct itemState (Store)
  const { isLoading, error, errors, user } = itemState;

  const item = user;

  const getUser = async () => {
    const params = {
      fields:
        "id,displayTitle,name,email,private_email,mobile,mobile_work,image_url,roles,project_accesses,approved,working_posistion,active,show_in_dropdown,show_on_website,department,user_type_id,hired_employee_supplier_id,hired_supplier.id,hired_supplier.displayTitle,hired_supplier.name,work_schedule_id,version,employment.*,user_children.*,user_next_of_kins.*,can_send_password_setup,last_invitation_sent_at,invitation_deliveries",
    };
    return fetchUser(id, params)
      .then((res) => {
        if (res.data.id) {
          setItemState((prevState) => ({
            ...prevState,
            isLoading: false,
            error: false,
            errors: [],
            user: { ...res.data, is_editable: res.data.is_editable !== false }, // Default to true if not set
          }));
        }
      })
      .catch((err) => {
        let errors = null;
        if (err?.data?.errors) {
          errors = mapErrors(err.data.errors);
        } else {
          errors = ["Something went wrong"];
        }
        console.error("Error fetching user:", errors); // Debug log
        setItemState((prevState) => ({
          ...prevState,
          isLoading: false,
          user: {},
          error: true,
          errors: errors,
        }));
      });
  };

  useEffect(() => {}, [itemState]);

  const onRefresh = (data) => {
    setItemState((prevState) => ({
      ...prevState,
      isLoading: false,
      // Preserve per-project grant metadata if an update response omits it, so the
      // Roles tab picker keeps its selected/inactive project state after saving.
      user:
        data && data.project_accesses === undefined && prevState.user
          ? { ...data, project_accesses: prevState.user.project_accesses }
          : data,
    }));
  };

  const [selectedTab, setSelectedTab] = useState(0);

  useEffect(() => {
    getUser();
  }, [dispatch, selectedTab]);

  // Innleid (hired, non-employee) users cannot have an employment, so the
  // Employment tab is hidden for them.
  const isHired = item?.user_type_id === 3;

  // Reasons the user won't appear in the employee/resource selectors (and other
  // missing setup). Used to render an explanatory notice on the detail page.
  const getVisibilityReasons = () => {
    if (!item?.id) return [];
    const reasons = [];
    if (!item?.active)
      reasons.push({
        key: "inactive",
        label: t("company.users.visibility.inactive"),
        hint: t("company.users.visibility.inactive_hint"),
      });
    if (!item?.show_in_dropdown)
      reasons.push({
        key: "not_in_dropdown",
        label: t("company.users.visibility.not_in_dropdown"),
        hint: t("company.users.visibility.not_in_dropdown_hint"),
      });
    if (isHired && !item?.hired_employee_supplier_id)
      reasons.push({
        key: "missing_supplier",
        label: t("company.users.visibility.missing_supplier"),
        hint: t("company.users.visibility.missing_supplier_hint"),
      });
    if (!isHired && !item?.employment)
      reasons.push({
        key: "not_employed",
        label: t("company.users.visibility.not_employed"),
        hint: t("company.users.visibility.not_employed_hint"),
      });
    if (!item?.roles || item.roles.length === 0)
      reasons.push({
        key: "no_roles",
        label: t("company.users.visibility.no_roles"),
        hint: t("company.users.visibility.no_roles_hint"),
      });
    const canHaveSchedule = isHired || item?.employment;
    if (canHaveSchedule && !item?.work_schedule_id) {
      reasons.push({
        key: "missing_work_schedule",
        label: t("company.users.visibility.missing_work_schedule"),
        hint: t("company.users.visibility.missing_work_schedule_hint"),
      });
    }
    return reasons;
  };

  const visibilityReasons = getVisibilityReasons();

  const tabs = [
    {
      name: "Detaljer",
      href: "#",
      component: (
        <>
          <UserInfo item={item} onRefresh={(data) => onRefresh(data)} />
          <UserPasswordAccess item={item} onReload={getUser} />
        </>
      ),
    },
    ...(isHired
      ? []
      : [
          {
            name: t("user_employment.tab"),
            href: "#",
            component: (
              <UserEmployment
                userItem={item}
                onRefresh={(data) => onRefresh(data)}
              />
            ),
          },
        ]),
    {
      name: t("user_work_schedule.tab"),
      href: "#",
      component: (
        <UserWorkSchedule
          userItem={item}
          onRefresh={(data) => onRefresh(data)}
        />
      ),
    },
    {
      name: t("company.userRoles.tab"),
      href: "#",
      component: (
        <UserRoles item={item} onRefresh={(data) => onRefresh(data)} />
      ),
    },
    {
      name: t("journal.journals"),
      href: "#",
      component: item?.id ? (
        <JournalSection subjectType="User" subjectId={item.id} />
      ) : null,
    },
    {
      name: t("textDocuments.title"),
      href: "#",
      component: item?.id ? (
        <TextDocumentsSection subjectType="User" subjectId={item.id} />
      ) : null,
    },
  ];

  const headerActions = [
    // {
    //   component: null,
    //   label: "Ge Robin en Øl",
    //   description: "",
    //   path: null,
    //   action: () => toast.success("Hej Robin"),
    //   icon: ArrowPathRoundedSquareIcon,
    //   textColor: "text-teal-700",
    //   bgColor: "bg-white",
    //   hidden: false,
    //   menu: true,
    // },
  ];

  function classNames(...classes) {
    return classes.filter(Boolean).join(" ");
  }

  function userTabs() {
    return (
      <div>
        <div className="sm:hidden">
          <label htmlFor="tabs" className="sr-only">
            Select a tab
          </label>
          {/* Use an "onChange" listener to redirect the user to the selected tab URL. */}
          <select
            onChange={(e) => setSelectedTab(e.target.value)}
            id="tabs"
            name="tabs"
            className="block w-full rounded-md border-gray-300 focus:border-indigo-500 focus:ring-indigo-500"
            defaultValue={tabs[selectedTab].name}
          >
            {tabs.map((tab, tabIdx) => (
              <option key={tab.name} value={tabIdx}>
                {tab.name}
              </option>
            ))}
          </select>
        </div>
        <div className="hidden sm:block">
          <nav
            className="isolate flex divide-x divide-gray-200 rounded-lg shadow"
            aria-label="Tabs"
          >
            {tabs.map((tab, tabIdx) => (
              <a
                key={tab.name}
                onClick={() => setSelectedTab(tabIdx)}
                className={classNames(
                  tabIdx === selectedTab
                    ? "text-gray-900"
                    : "text-gray-500 hover:text-gray-700",
                  tabIdx === 0 ? "rounded-l-lg" : "",
                  tabIdx === tabs.length - 1 ? "rounded-r-lg" : "",
                  "cursor-pointer group relative min-w-0 flex-1 overflow-hidden bg-white py-4 px-4 text-center text-sm font-medium hover:bg-gray-50 focus:z-10"
                )}
                aria-current={tabIdx === selectedTab ? "page" : undefined}
              >
                <span>{tab.name}</span>
                <span
                  aria-hidden="true"
                  className={classNames(
                    tabIdx === selectedTab ? "bg-indigo-500" : "bg-transparent",
                    "absolute inset-x-0 bottom-0 h-0.5"
                  )}
                />
              </a>
            ))}
          </nav>
        </div>
      </div>
    );
  }

  const renderContent = () => {
    if (isLoading) {
      return (
        <div className="">
          <ResourceLoading />
        </div>
      );
    }
    if (error && errors) {
      return <ResourceError errors={errors} />;
    }

    if (!item?.id) {
      return <ResourceEmpty />;
    }

    return (
      <>
        <DashboardHeader
          subtitle=""
          actions={headerActions}
          title={user?.displayTitle}
          meta={
            [
              // { label: "test", icon: Cog6ToothIcon }
            ]
          }
          //   mainButton={{
          //     label: "New",
          //     action: () => setCaseDialogOpen(true),
          //     path: null,
          //     color: "",
          //     icon: PlusIcon,
          //   }}
        >
          <div className="mx-auto mt-8 grid max-w-3xl grid-cols-1 gap-6 overflow-visible sm:px-6 lg:max-w-7xl lg:grid-flow-col-dense lg:grid-cols-3">
            <div className="space-y-6 overflow-visible lg:col-span-3 lg:col-start-1 sm:col-span-3">
              {visibilityReasons.length > 0 && (
                <PMNotice
                  severity="warning"
                  title={t("company.users.visibility.notice_title")}
                  description={
                    <div>
                      <p>{t("company.users.visibility.notice_intro")}</p>
                      <ul className="mt-2 list-disc space-y-1 pl-5">
                        {visibilityReasons.map((reason) => (
                          <li key={reason.key}>
                            <span className="font-medium">{reason.label}</span>
                            {reason.hint ? (
                              <span className="text-gray-600"> — {reason.hint}</span>
                            ) : null}
                          </li>
                        ))}
                      </ul>
                    </div>
                  }
                />
              )}
              {userTabs()}
              {tabs[selectedTab].component}
            </div>
          </div>
        </DashboardHeader>
      </>
    );
  };

  return <>{renderContent()}</>;
};
